import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Typography, Container, Box, Paper } from "@mui/material";
import { FaRandom } from "react-icons/fa"; // Example icon, can be replaced
import styles from "./error-page.module.scss";

// Define the structure of the joke object from the API
interface Joke {
    id: number;
    type: string;
    setup: string;
    punchline: string;
}

const ErrorPage: React.FC = () => {
    const [joke, setJoke] = useState<Joke | null>(null);
    const [loadingJoke, setLoadingJoke] = useState<boolean>(true);
    const [errorJoke, setErrorJoke] = useState<string | null>(null);

    // Function to fetch a random joke
    const fetchJoke = async () => {
        setLoadingJoke(true);
        setErrorJoke(null);
        try {
            const response = await fetch("https://official-joke-api.appspot.com/random_joke");
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data: Joke = await response.json();
            setJoke(data);
        } catch (err) {
            if (err instanceof Error) {
                setErrorJoke(`Sorry, couldn't fetch a joke right now. ${err.message}`);
            } else {
                setErrorJoke("Sorry, couldn't fetch a joke right now. An unknown error occurred.");
            }
            console.error("Failed to fetch joke:", err);
        } finally {
            setLoadingJoke(false);
        }
    };

    // Fetch joke on component mount
    useEffect(() => {
        fetchJoke();
    }, []);

    return (
        <Container maxWidth="md" className={styles.errorContainer}>
            <Paper elevation={3} className={styles.errorPaper}>
                <Typography variant="h1" component="h1" className={styles.errorCode}>
                    404
                </Typography>
                <Typography variant="h4" component="h2" className={styles.errorMessage}>
                    Oops! Page Not Found.
                </Typography>
                <Typography variant="body1" className={styles.errorDescription}>
                    It seems you&apos;ve wandered off the beaten path. Don&apos;t worry, it happens
                    to the best of us! While you&apos;re here, how about a joke?
                </Typography>

                <Box className={styles.jokeContainer}>
                    {loadingJoke && <Typography variant="body2">Loading a joke...</Typography>}
                    {errorJoke && (
                        <Typography variant="body2" color="error">
                            {errorJoke}
                        </Typography>
                    )}
                    {joke && !loadingJoke && !errorJoke && (
                        <>
                            <Typography variant="h6" className={styles.jokeSetup}>
                                {joke.setup}
                            </Typography>
                            <Typography variant="body1" className={styles.jokePunchline}>
                                {joke.punchline}
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={fetchJoke}
                        disabled={loadingJoke}
                        startIcon={<FaRandom />}
                        className={styles.newJokeButton}
                        aria-label="Fetch a new joke"
                        sx={{
                            marginTop: "1rem",
                        }}
                    >
                        Another Joke?
                    </Button>

                    <Typography
                        variant="body2"
                        className={styles.jokeDisclaimer}
                        sx={{
                            fontStyle: "italic",
                            color: "text.secondary",
                            opacity: 0.7,
                            marginTop: "1rem",
                        }}
                    >
                        Disclaimer: This joke is not from us, but from{" "}
                        <a
                            href="https://github.com/15Dkatz/official_joke_api"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            here
                        </a>
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    component={Link}
                    to="/newsfeed" // Assuming '/newsfeed' is the route. Please change if incorrect.
                    className={styles.homeButton}
                    aria-label="Go to Newsfeed page"
                >
                    Go to Newsfeed
                </Button>
            </Paper>
        </Container>
    );
};

export default ErrorPage;
